/**
 * Web Mercator tile maths.
 *
 * Shared by both maps, which draw OSM raster tiles as plain `<img>` elements.
 * That is the whole reason the venue card's map works everywhere: an image
 * grid needs no WebGL, no 1MB library, and nothing to initialise — it paints
 * at first render or not at all.
 */

export const TILE = 256

export interface Projected {
  /** Tile indices containing the point. */
  x: number
  y: number
  /** Where inside that tile the point falls, in pixels. */
  offsetX: number
  offsetY: number
}

/** Web Mercator → tile index plus the pixel offset within that tile. */
export function project(lat: number, lng: number, z: number): Projected {
  const n = 2 ** z
  const fx = ((lng + 180) / 360) * n
  const latRad = (lat * Math.PI) / 180
  const fy = ((1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2) * n

  return {
    x: Math.floor(fx),
    y: Math.floor(fy),
    offsetX: (fx - Math.floor(fx)) * TILE,
    offsetY: (fy - Math.floor(fy)) * TILE,
  }
}

/** The inverse: a point in continuous tile space back to coordinates. */
export function unproject(fx: number, fy: number, z: number): { lat: number; lng: number } {
  const n = 2 ** z
  const lng = (fx / n) * 360 - 180
  const lat = (Math.atan(Math.sinh(Math.PI * (1 - (2 * fy) / n))) * 180) / Math.PI
  return { lat, lng }
}

export function tileUrl(x: number, y: number, z: number): string {
  const n = 2 ** z
  // Wrap horizontally so panning past the date line still shows tiles; clamp
  // vertically, because there is nothing above the pole to show.
  const wrappedX = ((x % n) + n) % n
  return `https://tile.openstreetmap.org/${z}/${wrappedX}/${y}.png`
}

/** Metres per pixel at a given latitude and zoom — for a scale hint. */
export function metresPerPixel(lat: number, z: number): number {
  return (156543.03392 * Math.cos((lat * Math.PI) / 180)) / 2 ** z
}
